import get from 'lodash/get';
import differenceInDays from 'date-fns/difference_in_days';

// Relative Imports
import listingExtendedActions from '../actions/listingExtendedActions';
import edapActions from '../actions/edapActions';
import {log as logToServer} from '../api/logger';
import {discoveryFeedsByListingIdGraphQl} from '../api/discoveryFeedsByListingIdGraphQl';
import {unitSpacesGraphQl} from '../api/unitSpacesGraphQl';
import {urgencyMessageGraphQl} from '../api/urgencyMessageGraphQl';
import getUnitId from '../utils/getUnitId';
import {getRecommendedPropertiesGraphQl} from '../api/recommendedPropertiesGraphQl';
import {thingsOfInterestGraphQl} from '../api/thingsOfInterestGraphQl';
import {getIsIpmNonGuaranteedPricing} from '../selectors/listing';

let fetchCount = 0;

async function fetchUrgencyMessage(store) {
    // currently only needing this one time per page load
    fetchCount++;
    if (fetchCount > 1) {
        return;
    }

    // Added to prevent calls to urgency-message-service after urgency toaster showed up
    const state = store.getState();
    const {
        listingReducer: {unitApiUrl},
        systemReducer: {locale},
        listingPageReducer: {priceDetails},
        tripDetailsReducer: {arriveDateText, departDateText, adults, children, hasPets}
    } = state;
    const unitUuid = getUnitId(unitApiUrl, false);
    const options = {
        unitUuid,
        startDate: arriveDateText,
        endDate: departDateText,
        assetVersion: 3,
        locale,
        adults,
        children,
        hasPets,
        isIpmNonGuaranteedPricing: getIsIpmNonGuaranteedPricing(state)
    };
    if (get(priceDetails, 'size') > 0 && arriveDateText && departDateText) {
        const priceDetailsObject = priceDetails.toJS();
        const totalNightly = get(priceDetailsObject, 'priceDetails.averageNightlies', []).find(
            (nightly) => nightly.type === 'TOTAL_TRIP'
        );
        const nightlyAmount = get(totalNightly, 'perNightCost.amount');
        if (nightlyAmount) {
            const stayLength = differenceInDays(departDateText, arriveDateText);
            options.price = stayLength * nightlyAmount;
            options.currency = get(totalNightly, 'perNightCost.currency');
        }
    }
    let data = await urgencyMessageGraphQl(options);
    store.dispatch(listingExtendedActions.fetchUrgencyMessageSuccess(data));

    if (data) {
        store.dispatch(
            edapActions.triggerEdapEvent('generic.event', {
                eventaction: 'pdp urgency scarcity test',
                eventcategory: 'pdp page'
            })
        );
    }
}

function recommendedPropertiesLogNoResults(recoCarouselName, message) {
    logToServer({
        [`fetch${recoCarouselName}`]: true,
        [`fetch${recoCarouselName}HasResults`]: false,
        error: message || `${recoCarouselName} api returned no results`
    });
}

const RECO_CAROUSEL_TYPES = {
    BY_OWNER: 'ByOwnerProperties',
    INQUIRY: 'InquiryProperties',
    RECOMMENDED: 'RecommendedProperties'
};
const RECO_CAROUSEL_FETCH_SUCCESS = {
    [RECO_CAROUSEL_TYPES.BY_OWNER]: listingExtendedActions.fetchByOwnerPropertiesSuccess,
    [RECO_CAROUSEL_TYPES.INQUIRY]: listingExtendedActions.fetchInquiryPropertiesSuccess,
    [RECO_CAROUSEL_TYPES.RECOMMENDED]: listingExtendedActions.fetchRecommendedPropertiesSuccess
};
const RECO_CAROUSEL_FETCH_FAIL = {
    [RECO_CAROUSEL_TYPES.BY_OWNER]: listingExtendedActions.fetchByOwnerPropertiesFailed,
    [RECO_CAROUSEL_TYPES.INQUIRY]: listingExtendedActions.fetchInquiryPropertiesFailed,
    [RECO_CAROUSEL_TYPES.RECOMMENDED]: listingExtendedActions.fetchRecommendedPropertiesFailed
};

function fetchRecoProperties(store, recoCarouselName, params) {
    const {
        listingReducer: {listingId},
        tripDetailsReducer: {arriveDateText, departDateText},
        systemReducer: {siteName}
    } = store.getState();

    return getRecommendedPropertiesGraphQl({
        listingTriads: listingId,
        startDate: arriveDateText,
        endDate: departDateText,
        site: siteName,
        size: 10,
        ...params
    })
        .then(
            ({
                recommendedListingResponse: {
                    message: {carouselTitle: recommendedPropertiesAlternativeTitle},
                    clientRequestId,
                    recommendedListings: recommendedProperties
                }
            }) => {
                if (!recommendedProperties || !(recommendedProperties.length >= 0)) {
                    store.dispatch(RECO_CAROUSEL_FETCH_FAIL[recoCarouselName]());
                    return;
                }
                if (recommendedProperties.length === 0) {
                    recommendedPropertiesLogNoResults(recoCarouselName, 'zero results');
                }

                store.dispatch(
                    RECO_CAROUSEL_FETCH_SUCCESS[recoCarouselName]({
                        recommendedProperties,
                        recommendedPropertiesAlternativeTitle,
                        clientRequestId
                    })
                );
            }
        )
        .catch((error) => {
            store.dispatch(RECO_CAROUSEL_FETCH_FAIL[recoCarouselName]());
            recommendedPropertiesLogNoResults(recoCarouselName, error);
        });
}

function fetchByOwnerProperties(store) {
    return fetchRecoProperties(store, RECO_CAROUSEL_TYPES.BY_OWNER, {
        target: 'PDP_SAME_OWNER_PROPERTY'
    });
}

function fetchRecommendedProperties(store) {
    return fetchRecoProperties(store, RECO_CAROUSEL_TYPES.RECOMMENDED, {
        target: 'PDP_SIMILAR_PROPERTY',
        fallback: false
    });
}

function fetchInquiryProperties(store) {
    return fetchRecoProperties(store, RECO_CAROUSEL_TYPES.INQUIRY, {
        target: 'INQUIRY_SIMILAR_PROPERTY',
        fallback: false
    });
}

function fetchDiscoveryFeed(store) {
    const {
        listingReducer: {listingId},
        systemReducer: {locale}
    } = store.getState();

    return discoveryFeedsByListingIdGraphQl({listingId, locale}).then((data) => {
        const discoveryFeedData = data && data.discoveryFeedsByListingId;

        store.dispatch(listingExtendedActions.fetchDiscoveryFeedSuccess(discoveryFeedData));
    });
}

function fetchUnitSpaces(store) {
    const {
        listingReducer: {listingId},
        systemReducer: {locale}
    } = store.getState();

    return unitSpacesGraphQl({listingId, locale}).then((data) => {
        const unitSpacesData = data && data.unitSpaces;

        store.dispatch(listingExtendedActions.fetchUnitSpacesSuccess(unitSpacesData));
    });
}

async function fetchThingsOfInterest(store) {
    const {
        listingReducer: {geoCode, geography},
        systemReducer: {locale}
    } = store.getState();
    const location = (geography && geography.location) || {};
    const geo = geoCode || {};
    const lat = geo.latitude || location.lat;
    const lng = geo.longitude || location.lng;
    const params = {
        position: `${lat}, ${lng}`,
        size: 30,
        locale
    };
    const data = await thingsOfInterestGraphQl(params);

    if (data.errorCode) return;
    const foursquare = data && data.Foursquare ? data.Foursquare : [];
    const gaia = data && data.Gaia ? data.Gaia : [];
    const thingsOfInterest = [...foursquare, ...gaia];

    store.dispatch(listingExtendedActions.fetchThingsOfInterestSuccess(thingsOfInterest));
}
export default fetchUrgencyMessage;

export {
    fetchByOwnerProperties,
    fetchRecommendedProperties,
    fetchInquiryProperties,
    recommendedPropertiesLogNoResults,
    fetchUnitSpaces,
    fetchUrgencyMessage,
    fetchDiscoveryFeed,
    fetchThingsOfInterest
};
